import type React from "react";
import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  FormControl,
  MenuItem,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import type { RootState } from "../../../store/Index";
import apiService from "../../../service/Api";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../components/common/ShowToast";
import type { AxiosResponse } from "axios";
import type { IStreamFormData } from "../../../types/streams";
import CustomSelect from "../../../components/common/Select";
import { FormatCrew } from "../utils/FormatCrew";
import AssignmentRateDialog from "../../crew-management/components/AssignmentRateDialog";
import MissingRoleRateDialog from "../../crew-management/components/MissingRoleRateDialog";
import useAssignmentRateGuard from "../../crew-management/hooks/useAssignmentRateGuard";
import {
  AssignmentRateResolution,
  createPixelPilotJobAndGig,
} from "../../crew-management/utils/assignmentRates";

interface CrewMember {
  id: number;
  user: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    profile_picture: string | null;
    individual: {
      phone_number: string | null;
    } | null;
  };
  roles: { id: number; name: string }[];
  tools: { id: number; name: string }[];
  skills: { id: number; name: string }[];
  experience_years: number;
  bio: string;
}

interface Role {
  id: number;
  name: string;
}

interface Tool {
  id: number;
  name: string;
}

interface Skill {
  id: number;
  name: string;
}

export interface CallSheetEntry {
  id: number;
  crew_member: CrewMember;
  assignment: Array<{
    role: string;
    notes: string;
  }>;
  created_at: string;
  updated_at: string;
  stream: number;
}

interface Props {
  formData: IStreamFormData;
  setFormData: React.Dispatch<React.SetStateAction<IStreamFormData>>;
}

const AddStreamTeamAssignment: React.FC<Props> = ({
  formData,
  setFormData,
}) => {
  const { id: streamId } = useParams<{ id?: string }>();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const [crewMembers, setCrewMembers] = useState<CrewMember[]>([]);
  const [rolesList, setRolesList] = useState<Role[]>([]);
  const [, setTools] = useState<Tool[]>([]);
  const [, setSkills] = useState<Skill[]>([]);
  const [callSheetEntries, setCallSheetEntries] = useState<CallSheetEntry[]>(
    []
  );
  const [, setSelectedCrewMember] = useState<CrewMember | null>(null);
  const [loading, setLoading] = useState(false);
  const [savingMember, setSavingMember] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [newMember, setNewMember] = useState<{
    crew_member: number;
    role: number;
    tools: number[];
    skills: number[];
    assignment: { type: string; details: string };
  }>({
    crew_member: 0,
    role: 0,
    tools: [],
    skills: [],
    assignment: { type: "", details: "" },
  });
  const {
    ensureRateForRole,
    missingRateDialogProps,
    assignmentRateDialogProps,
  } = useAssignmentRateGuard();

  useEffect(() => {
    setLoading(true);
    const promises: Array<Promise<AxiosResponse<unknown>>> = [
      apiService.get<{ results: CrewMember[] }>("/api/crews/crew-members/"),
      apiService.get<{ results: Role[] }>("/api/crews/roles/"),
      apiService.get<{ results: Tool[] }>("/api/crews/tools/"),
      apiService.get<{ results: Skill[] }>("/api/crews/skills/"),
    ];

    if (streamId) {
      promises.push(
        apiService.get<{ results: CallSheetEntry[] }>(
          `/api/call-sheets/callsheet/?stream=${streamId}&page=1&page_size=20`
        )
      );
    }

    Promise.all(promises)
      .then((responses) => {
        const crewRes = responses[0] as AxiosResponse<{
          results: CrewMember[];
        }>;
        const rolesRes = responses[1] as AxiosResponse<{ results: Role[] }>;
        const toolsRes = responses[2] as AxiosResponse<{ results: Tool[] }>;
        const skillsRes = responses[3] as AxiosResponse<{ results: Skill[] }>;
        const callSheetsRes = streamId
          ? (responses[4] as AxiosResponse<{ results: CallSheetEntry[] }>)
          : undefined;

        setCrewMembers(crewRes.data.results || []);
        setRolesList(rolesRes.data.results || []);
        setTools(toolsRes.data.results || []);
        setSkills(skillsRes.data.results || []);

        if (callSheetsRes && streamId) {
          setCallSheetEntries(callSheetsRes.data.results || []);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch data:", err);
        showErrorToast(
          "Failed to load team assignment data. Please try again."
        );
      })
      .finally(() => setLoading(false));
  }, [streamId]);

  // Open modal and reset new member state
  const handleOpenModal = () => {
    setNewMember({
      crew_member: 0,
      role: 0,
      tools: [],
      skills: [],
      assignment: { type: "", details: "" },
    });
    setSelectedCrewMember(null);
    setOpenModal(true);
  };

  // Close modal
  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const completeSaveMember = async (
    resolvedRate: AssignmentRateResolution,
  ) => {
    if (newMember.crew_member === 0 || newMember.role === 0) {
      showErrorToast("Please select a crew member and role.");
      return;
    }

    try {
      const selectedRole = rolesList.find((r) => r.id === newMember.role);
      const selectedCrewMember = crewMembers.find(
        (crewMember) => crewMember.id === newMember.crew_member,
      );

      if (!selectedRole) {
        showErrorToast("Please select a valid role.");
        return;
      }

      if (!selectedCrewMember) {
        showErrorToast("Please select a valid crew member.");
        return;
      }

      const alreadyAssigned = callSheetEntries.some((entry) => {
        const matchesCrew = entry.crew_member.id === newMember.crew_member;
        const matchesRole = entry.assignment?.some(
          (assignment) =>
            assignment.role?.trim().toLowerCase() ===
            selectedRole.name.trim().toLowerCase(),
        );

        return matchesCrew && matchesRole;
      });

      if (alreadyAssigned) {
        showErrorToast(
          `${selectedCrewMember.user.first_name} ${selectedCrewMember.user.last_name} is already assigned as ${selectedRole.name} on this callsheet.`
        );
        return;
      }

      const payload = {
        stream_id: Number.parseInt(streamId || "0"),
        crew_member_id: newMember.crew_member,
        roles_ids: [selectedRole.id],
        assignment: [
          {
            role: selectedRole?.name || "",
            notes: "",
          },
        ],
      };

      const assignmentResponse = await apiService.post<{ id: number }>(
        "/api/call-sheets/callsheet/",
        payload
      );

      await createPixelPilotJobAndGig({
        amount: resolvedRate.amount,
        assignedTo: selectedCrewMember.user.id,
        callsheetId: assignmentResponse.data.id,
        crewMemberId: selectedCrewMember.id,
        currency: resolvedRate.currency,
        description: formData.title,
        stream: {
          id: Number.parseInt(streamId || "0"),
          title: formData.title || "",
          date: formData.date,
          start: formData.startTime,
          end: formData.endTime,
        },
      });

      // Refresh the call sheet entries
      const updatedResponse = await apiService.get<{
        results: CallSheetEntry[];
      }>(`/api/call-sheets/callsheet/?stream=${streamId}&page=1&page_size=20`);
      setCallSheetEntries(updatedResponse.data.results || []);

      setOpenModal(false);
      setFormData((prev) => ({
        ...prev,
        crew: {
          ...prev.crew,
          members: [
            ...prev.crew.members,
            {
              crew_member: newMember.crew_member,
              role: newMember.role,
              tools: newMember.tools,
              skills: newMember.skills,
              assignment: newMember.assignment,
            },
          ],
        },
      }));
      showSuccessToast("Crew member added successfully!");
    } catch (error) {
      console.error("Failed to add crew member:", error);
      showErrorToast("Failed to add crew member. Please try again.");
    }
  };

  const handleSaveMember = async () => {
    if (newMember.crew_member === 0 || newMember.role === 0) {
      showErrorToast("Please select a crew member and role.");
      return;
    }

    const selectedRole = rolesList.find((role) => role.id === newMember.role);

    if (!selectedRole) {
      showErrorToast("Please select a valid role.");
      return;
    }

    const selectedCrewMember = crewMembers.find(
      (crewMember) => crewMember.id === newMember.crew_member,
    );
    const alreadyAssigned = callSheetEntries.some((entry) => {
      const matchesCrew = entry.crew_member.id === newMember.crew_member;
      const matchesRole = entry.assignment?.some(
        (assignment) =>
          assignment.role?.trim().toLowerCase() ===
          selectedRole.name.trim().toLowerCase(),
      );

      return matchesCrew && matchesRole;
    });

    if (alreadyAssigned) {
      showErrorToast(
        `${selectedCrewMember?.user.first_name || "This crew member"} ${
          selectedCrewMember?.user.last_name || ""
        } is already assigned as ${selectedRole.name} on this callsheet.`
      );
      return;
    }

    setSavingMember(true);

    try {
      const hasRate = await ensureRateForRole(
        {
          id: selectedRole.id,
          name: selectedRole.name,
        },
        async (resolvedRate) => {
          setSavingMember(true);
          try {
            await completeSaveMember(resolvedRate);
          } finally {
            setSavingMember(false);
          }
        },
        {
          effectiveDate: formData.date,
        },
      );

      if (!hasRate) {
        setSavingMember(false);
      }
    } catch (error) {
      console.error("Failed to validate the selected role rate:", error);
      showErrorToast("Failed to validate the selected role rate. Please try again.");
      setSavingMember(false);
    }
  };

  const removeCrewMember = async (entry: CallSheetEntry) => {
    try {
      await apiService.delete(`/api/call-sheets/callsheet/${entry.id}/`);

      // Remove from local state
      setCallSheetEntries((prev) =>
        prev.filter((item) => item.id !== entry.id)
      );
      setFormData((prev) => ({
        ...prev,
        crew: {
          ...prev.crew,
          members: prev.crew.members.filter(
            (member) => member.crew_member !== entry.crew_member.id
          ),
        },
      }));
      showSuccessToast("Crew member removed successfully!");
    } catch (error) {
      console.error("Failed to remove crew member:", error);
      showErrorToast("Failed to remove crew member. Please try again.");
    }
  };

  // Update new member data
  const updateNewMember = <K extends keyof typeof newMember>(
    field: K,
    value: typeof newMember[K]
  ) => {
    if (field === "assignment") {
      setNewMember((prev) => ({
        ...prev,
        assignment: value as typeof newMember.assignment,
      }));
      return;
    }
    setNewMember((prev) => ({ ...prev, [field]: value }));
  };

  // Handle crew member selection in modal
  const handleCrewChange = (crewMember: CrewMember | null) => {
    if (crewMember) {
      setSelectedCrewMember(crewMember);
      updateNewMember("crew_member", crewMember.id);
    } else {
      setSelectedCrewMember(null);
      updateNewMember("crew_member", 0);
    }
  };

  const sortedCrew = FormatCrew(callSheetEntries);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={5}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Card sx={{ boxShadow: 0, height: "100%" }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Team Assignment ({formData.crew.members.length})
            </Typography>
            <Button
              variant="outlined"
              sx={{
                borderColor: reduxTheme.primary_colors[100],
                border: 2,
                color: reduxTheme.primary_colors[100],
              }}
              onClick={handleOpenModal}
            >
              Add Crew Member
            </Button>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor:
                      reduxTheme.primary_colors?.[800] || "#1976d2",
                  }}
                >
                  <TableCell
                    sx={{
                      color: reduxTheme.primary_colors?.[100] || "white",
                      fontWeight: "bold",
                    }}
                  >
                    Role
                  </TableCell>
                  <TableCell
                    sx={{
                      color: reduxTheme.primary_colors?.[100] || "white",
                      fontWeight: "bold",
                    }}
                  >
                    Name
                  </TableCell>
                  <TableCell
                    sx={{
                      color: reduxTheme.primary_colors?.[100] || "white",
                      fontWeight: "bold",
                    }}
                  >
                    Email
                  </TableCell>
                  <TableCell
                    sx={{
                      color: reduxTheme.primary_colors?.[100] || "white",
                      fontWeight: "bold",
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedCrew.map((entry, index) => {
                  const user = entry.crew_member.user;
                  const role = entry.assignment?.[0]?.role || "N/A";
                  const fullName =
                    `${user.first_name} ${user.last_name}`.trim();
                  const email = user.email || "";

                  return (
                    <TableRow
                      key={entry.id}
                      sx={{
                        backgroundColor:
                          index % 2 === 0
                            ? "transparent"
                            : reduxTheme.primary_colors?.[800] || "#f5f5f5",
                        "&:hover": {
                          backgroundColor:
                            reduxTheme.primary_colors?.[800] || "#e3f2fd",
                        },
                      }}
                    >
                      <TableCell sx={{ fontSize: "0.85rem" }}>{role}</TableCell>
                      <TableCell sx={{ fontSize: "0.85rem" }}>
                        {fullName}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.85rem" }}>
                        {email}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          color="error"
                          onClick={() => removeCrewMember(entry)}
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {callSheetEntries.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ textAlign: "center", py: 3 }}>
                      <Typography variant="body2" color="textSecondary">
                        No crew members assigned yet. Click &quot;Add Crew
                        Member&quot; to get started.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Modal for adding crew member */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add Crew Member</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <CustomSelect
                value={newMember.role}
                label="Role"
                onChange={(e) =>
                  updateNewMember("role", Number(e.target.value))
                }
              >
                <MenuItem value={0}>Select Role</MenuItem>
                {rolesList.map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    {role.name}
                  </MenuItem>
                ))}
              </CustomSelect>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <CustomSelect
                value={newMember.crew_member}
                label="Crew Member"
                onChange={(e) =>
                  handleCrewChange(
                    crewMembers.find(
                      (cm) => cm.id === Number(e.target.value)
                    ) || null
                  )
                }
              >
                <MenuItem value={0}>Select Crew Member</MenuItem>
                {crewMembers.map((cm) => (
                  <MenuItem key={cm.id} value={cm.id}>
                    {`${cm.user.first_name} ${cm.user.last_name}`.trim() ||
                      cm.user.email}
                  </MenuItem>
                ))}
              </CustomSelect>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancel</Button>
          <Button onClick={handleSaveMember} variant="contained" disabled={savingMember}>
            {savingMember ? (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={18} color="inherit" />
                Adding...
              </Box>
            ) : (
              "Add Member"
            )}
          </Button>
        </DialogActions>
      </Dialog>
      <MissingRoleRateDialog {...missingRateDialogProps} />
      <AssignmentRateDialog {...assignmentRateDialogProps} />
    </Box>
  );
};

export default AddStreamTeamAssignment;
